import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowLeft, Check, Plus, Filter, ShoppingCart, Package,
  Flame, Snowflake, Droplets, Armchair, Wind, FlameKindling,
  Zap, UtensilsCrossed, CreditCard, Bell, Coffee, Settings,
  CupSoda, Droplet, Cake, Ruler, GlassWater, Beer, Truck,
  type LucideIcon,
} from "lucide-react";

// Map equipment emoji → Lucide icon for monochrome rendering
const EMOJI_ICON_MAP: Record<string, LucideIcon> = {
  "🔥": Flame,
  "🧊": Snowflake,
  "🫧": Droplets,
  "🪑": Armchair,
  "💨": Wind,
  "🧯": FlameKindling,
  "⚡": Zap,
  "🍟": UtensilsCrossed,
  "❄️": Snowflake,
  "💳": CreditCard,
  "🛎️": Bell,
  "☕": Coffee,
  "⚙️": Settings,
  "🫖": CupSoda,
  "💧": Droplet,
  "🧁": Cake,
  "🍞": Cake,
  "📏": Ruler,
  "🍸": GlassWater,
  "🍺": Beer,
  "🚚": Truck,
  "🍳": UtensilsCrossed,
};

const getIconForItem = (emoji: string): LucideIcon => EMOJI_ICON_MAP[emoji] || Package;
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { useAppContext } from "@/lib/onboardingContext";
import { getCurrencySymbol, convertFromUSD, formatCurrency } from "@/lib/currency";
import { getEquipmentForBusinessType, getCategories, PRIORITIES, type EquipmentItem, type Priority } from "@/lib/equipmentData";

const priorityColor = (p: Priority) => {
  switch (p) {
    case "essential": return "bg-primary/10 text-primary border-primary/20";
    case "recommended": return "bg-accent/10 text-accent border-accent/20";
    case "optional": return "bg-muted text-muted-foreground border-border";
  }
};

export default function Equipment() {
  const navigate = useNavigate();
  const { onboarding } = useAppContext();
  const city = onboarding?.city || "miami";
  const cs = getCurrencySymbol(city);

  const defaultItems = useMemo(() => getEquipmentForBusinessType(onboarding?.businessType || "restaurant"), [onboarding?.businessType]);
  const categories = useMemo(() => getCategories(defaultItems), [defaultItems]);

  const [items, setItems] = useState<EquipmentItem[]>(defaultItems);
  const [checked, setChecked] = useState<Set<string>>(new Set());
  const [categoryFilter, setCategoryFilter] = useState<string>("all");
  const [priorityFilter, setPriorityFilter] = useState<string>("all");
  const [addOpen, setAddOpen] = useState(false);
  const [newItem, setNewItem] = useState({ name: "", emoji: "📦", priceMin: "", priceMax: "", priority: "recommended" as Priority, tip: "", whereToBuy: "", category: categories[0] || "Kitchen" });

  const filtered = useMemo(() => {
    return items.filter(i =>
      (categoryFilter === "all" || i.category === categoryFilter) &&
      (priorityFilter === "all" || i.priority === priorityFilter)
    );
  }, [items, categoryFilter, priorityFilter]);

  const toggle = (id: string) => {
    setChecked(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const totalItems = items.length;
  const acquiredCount = checked.size;
  const totalEstimatedCost = useMemo(() => {
    return items.filter(i => !i.isMonthly).reduce((sum, i) => sum + (i.priceRange[0] + i.priceRange[1]) / 2, 0);
  }, [items]);
  const acquiredCost = useMemo(() => {
    return items.filter(i => checked.has(i.id) && !i.isMonthly).reduce((sum, i) => sum + (i.priceRange[0] + i.priceRange[1]) / 2, 0);
  }, [items, checked]);

  const addCustomItem = () => {
    if (!newItem.name.trim()) { toast.error("Item name is required"); return; }
    const id = `custom-${Date.now()}`;
    setItems(prev => [...prev, {
      id, emoji: newItem.emoji || "📦", name: newItem.name,
      priceRange: [Number(newItem.priceMin) || 0, Number(newItem.priceMax) || 0],
      priority: newItem.priority, tip: newItem.tip, whereToBuy: newItem.whereToBuy,
      category: newItem.category, custom: true,
    }]);
    setNewItem({ name: "", emoji: "📦", priceMin: "", priceMax: "", priority: "recommended", tip: "", whereToBuy: "", category: categories[0] || "Kitchen" });
    setAddOpen(false);
    toast.success("Item added!");
  };

  const progressPct = totalItems > 0 ? Math.round((acquiredCount / totalItems) * 100) : 0;

  const fmtPrice = (usd: number) => `${cs}${convertFromUSD(usd, city).toLocaleString()}`;

  return (
    <div className="min-h-screen bg-background pb-16">
      {/* Header */}
      <div className="border-b border-border bg-card">
        <div className="container py-4 flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate("/dashboard")}><ArrowLeft className="w-5 h-5" /></Button>
          <div>
            <h1 className="font-heading text-xl font-medium text-foreground">Equipment & Inventory</h1>
            <p className="text-sm text-muted-foreground">Everything you need before opening day</p>
          </div>
        </div>
      </div>

      <div className="container pt-6 space-y-6">
        {/* Progress Card */}
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
          className="bg-card rounded-xl p-5 border border-border shadow-card">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Package className="w-5 h-5 text-primary" />
              <span className="font-heading font-medium text-foreground">Progress</span>
            </div>
            <span className="text-sm font-medium text-primary">{acquiredCount} of {totalItems} acquired</span>
          </div>
          <div className="w-full h-3 bg-muted rounded-full overflow-hidden mb-3">
            <motion.div className="h-full bg-primary rounded-full" initial={{ width: 0 }} animate={{ width: `${progressPct}%` }} transition={{ duration: 0.5 }} />
          </div>
          <div className="flex justify-between text-xs text-muted-foreground">
            <span>Acquired: ~{formatCurrency(convertFromUSD(acquiredCost, city), city)}</span>
            <span>Est. Total: ~{formatCurrency(convertFromUSD(totalEstimatedCost, city), city)}</span>
          </div>
        </motion.div>

        {/* Filters + Add */}
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }}
          className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-muted-foreground" />
            <Select value={categoryFilter} onValueChange={setCategoryFilter}>
              <SelectTrigger className="h-8 text-xs w-[160px]"><SelectValue placeholder="Category" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                {categories.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={priorityFilter} onValueChange={setPriorityFilter}>
              <SelectTrigger className="h-8 text-xs w-[140px]"><SelectValue placeholder="Priority" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Priorities</SelectItem>
                {PRIORITIES.map(p => <SelectItem key={p} value={p}>{p.charAt(0).toUpperCase() + p.slice(1)}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <Dialog open={addOpen} onOpenChange={setAddOpen}>
            <DialogTrigger asChild>
              <Button size="sm" variant="outline" className="gap-1 ml-auto"><Plus className="w-3.5 h-3.5" /> Add Item</Button>
            </DialogTrigger>
            <DialogContent className="max-w-md">
              <DialogHeader><DialogTitle>Add Custom Item</DialogTitle></DialogHeader>
              <div className="space-y-3 pt-2">
                <div className="flex gap-2">
                  <div className="w-16">
                    <Label className="text-xs">Emoji</Label>
                    <Input value={newItem.emoji} onChange={e => setNewItem(p => ({ ...p, emoji: e.target.value }))} className="text-center" />
                  </div>
                  <div className="flex-1">
                    <Label className="text-xs">Name *</Label>
                    <Input value={newItem.name} onChange={e => setNewItem(p => ({ ...p, name: e.target.value }))} placeholder="e.g. Blender" />
                  </div>
                </div>
                <div className="flex gap-2">
                  <div className="flex-1">
                    <Label className="text-xs">Min Price ({cs})</Label>
                    <Input type="number" value={newItem.priceMin} onChange={e => setNewItem(p => ({ ...p, priceMin: e.target.value }))} />
                  </div>
                  <div className="flex-1">
                    <Label className="text-xs">Max Price ({cs})</Label>
                    <Input type="number" value={newItem.priceMax} onChange={e => setNewItem(p => ({ ...p, priceMax: e.target.value }))} />
                  </div>
                </div>
                <div className="flex gap-2">
                  <div className="flex-1">
                    <Label className="text-xs">Category</Label>
                    <Select value={newItem.category} onValueChange={v => setNewItem(p => ({ ...p, category: v }))}>
                      <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                      <SelectContent>{categories.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="flex-1">
                    <Label className="text-xs">Priority</Label>
                    <Select value={newItem.priority} onValueChange={v => setNewItem(p => ({ ...p, priority: v as Priority }))}>
                      <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                      <SelectContent>{PRIORITIES.map(p => <SelectItem key={p} value={p}>{p.charAt(0).toUpperCase() + p.slice(1)}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <Label className="text-xs">Pro Tip</Label>
                  <Input value={newItem.tip} onChange={e => setNewItem(p => ({ ...p, tip: e.target.value }))} placeholder="Optional tip" />
                </div>
                <div>
                  <Label className="text-xs">Where to Buy</Label>
                  <Input value={newItem.whereToBuy} onChange={e => setNewItem(p => ({ ...p, whereToBuy: e.target.value }))} placeholder="e.g. Amazon, Restaurant Depot" />
                </div>
                <Button onClick={addCustomItem} className="w-full gap-2"><Plus className="w-4 h-4" /> Add Item</Button>
              </div>
            </DialogContent>
          </Dialog>
        </motion.div>

        {/* Items by Category */}
        {categories.filter(cat => categoryFilter === "all" || categoryFilter === cat).map(cat => {
          const catItems = filtered.filter(i => i.category === cat);
          if (catItems.length === 0) return null;
          return (
            <motion.div key={cat} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
              <h2 className="font-heading font-medium text-foreground mb-3 flex items-center gap-2">
                {cat}
                <span className="text-xs font-normal text-muted-foreground">({catItems.filter(i => checked.has(i.id)).length}/{catItems.length})</span>
              </h2>
              <div className="space-y-2">
                {catItems.map(item => {
                  const isChecked = checked.has(item.id);
                  const ItemIcon = getIconForItem(item.emoji);
                  return (
                    <div key={item.id}
                      onClick={() => toggle(item.id)}
                      className={`bg-card rounded-xl p-4 border shadow-card cursor-pointer transition-all ${isChecked ? "border-primary/40 bg-primary/5" : "border-border hover:border-primary/20"}`}>
                      <div className="flex items-start gap-3">
                        <div className="w-8 h-8 flex items-center justify-center shrink-0">
                          {isChecked ? (
                            <Check className="w-5 h-5 text-primary" />
                          ) : (
                            <ItemIcon className="w-5 h-5" strokeWidth={1.75} style={{ color: "#4a4a4a" }} />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className={`text-sm font-medium ${isChecked ? "line-through text-muted-foreground" : "text-foreground"}`}>{item.name}</span>
                            {item.custom && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-muted text-muted-foreground">custom</span>}
                          </div>
                          <div className="text-xs text-muted-foreground mt-0.5">
                            {fmtPrice(item.priceRange[0])}–{fmtPrice(item.priceRange[1])}{item.isMonthly ? "/mo" : ""}
                          </div>
                          {item.tip && <p className="text-xs text-muted-foreground mt-1 leading-relaxed">💡 {item.tip}</p>}
                          {item.whereToBuy && (
                            <div className="flex items-center gap-1 mt-1 text-[11px] text-muted-foreground">
                              <ShoppingCart className="w-3 h-3" /> {item.whereToBuy}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Add custom item per category */}
              <Button
                size="sm" variant="ghost"
                className="gap-1.5 mt-2 text-xs text-muted-foreground hover:text-primary"
                onClick={(e) => { e.stopPropagation(); setNewItem(p => ({ ...p, category: cat })); setAddOpen(true); }}
              >
                <Plus className="w-3 h-3" /> Add Custom Item
              </Button>
            </motion.div>
          );
        })}
      </div>
    </div>
  );
}
